import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  Calculator, 
  Plus, 
  Trash2, 
  Save, 
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  Building,
  MapPin,
  Ruler,
  Wrench,
  FileText,
  Loader2,
  Check,
  Clock,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Comparison options
const COMPARISON_OPTIONS = [
  { value: 'superior', label: 'Superior', icon: TrendingUp, color: 'text-green-600' },
  { value: 'similar', label: 'Similar', icon: Minus, color: 'text-gray-600' },
  { value: 'inferior', label: 'Inferior', icon: TrendingDown, color: 'text-red-600' }
];

const SIZE_OPTIONS = [
  { value: 'superior', label: 'Larger', icon: TrendingUp, color: 'text-green-600' },
  { value: 'similar', label: 'Similar', icon: Minus, color: 'text-gray-600' },
  { value: 'inferior', label: 'Smaller', icon: TrendingDown, color: 'text-red-600' }
];

const HIERARCHY_OPTIONS = [
  { value: 'primary', label: 'Primary', weight: 3 },
  { value: 'secondary', label: 'Secondary', weight: 2 },
  { value: 'supporting', label: 'Supporting', weight: 1 }
];

/**
 * ComparableAnalysisMatrix Component
 * 
 * Professional valuation analysis tool that links to existing Comparable Schedule
 * and allows valuers to manually analyse comparables and arrive at an opinion of value.
 */
export default function ComparableAnalysisMatrix({ 
  inspectionId, 
  onValuationCalculated,
  subjectProperty,
  disabled = false 
}) {
  const { token } = useContext(AuthContext);
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [editedEntries, setEditedEntries] = useState({});
  const [valuationRange, setValuationRange] = useState({
    lowest_adjusted: null,
    highest_adjusted: null,
    average_adjusted: null,
    weighted_average: null,
    suggested_range_low: null,
    suggested_range_high: null,
    final_opinion_value: '',
    final_opinion_notes: ''
  });
  const [includeInReport, setIncludeInReport] = useState(false);
  const [exportAsWorkingPapers, setExportAsWorkingPapers] = useState(true);
  
  // Auto-save state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const lastSavedDataRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);
  const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
  
  // Load matrix on mount
  useEffect(() => {
    if (inspectionId) {
      loadMatrix();
    }
  }, [inspectionId]);
  
  const loadMatrix = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analysis-matrix/${inspectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMatrix(response.data.matrix);
        setValuationRange(response.data.matrix.valuation_range || {});
        setIncludeInReport(response.data.matrix.include_in_report || false);
        setExportAsWorkingPapers(response.data.matrix.export_as_working_papers ?? true);
        
        // Initialize edited entries
        const entries = {};
        response.data.matrix.comparable_analyses?.forEach(entry => {
          entries[entry.comparable_id] = { ...entry };
        });
        setEditedEntries(entries);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load analysis matrix'));
    } finally {
      setLoading(false);
    }
  };
  
  // Track changes for auto-save
  useEffect(() => {
    if (!matrix || !lastSavedDataRef.current) return;
    
    const currentData = JSON.stringify({
      editedEntries,
      valuationRange,
      includeInReport,
      exportAsWorkingPapers
    });
    
    const savedData = JSON.stringify(lastSavedDataRef.current);
    
    if (currentData !== savedData) {
      setHasUnsavedChanges(true);
    }
  }, [editedEntries, valuationRange, includeInReport, exportAsWorkingPapers, matrix]);

  // Initialize lastSavedDataRef when data is loaded
  useEffect(() => {
    if (matrix && !lastSavedDataRef.current) {
      lastSavedDataRef.current = {
        editedEntries,
        valuationRange,
        includeInReport,
        exportAsWorkingPapers
      };
    }
  }, [matrix, editedEntries, valuationRange, includeInReport, exportAsWorkingPapers]);

  // Auto-save all entries function
  const performAutoSave = useCallback(async () => {
    if (!inspectionId || !hasUnsavedChanges || saving || disabled) {
      return;
    }
    
    setAutoSaveStatus('saving');
    
    try {
      // Save all edited entries
      const savePromises = Object.entries(editedEntries).map(async ([comparableId, entry]) => {
        const totals = calculateEntryTotals(entry);
        const updatedEntry = { ...entry, ...totals };
        
        await axios.put(
          `${API}/analysis-matrix/${inspectionId}/entry/${comparableId}`,
          updatedEntry,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        return { comparableId, entry: updatedEntry };
      });
      
      await Promise.all(savePromises);
      
      // Save valuation range
      await axios.put(
        `${API}/analysis-matrix/${inspectionId}/valuation-range`,
        valuationRange,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update last saved reference
      lastSavedDataRef.current = {
        editedEntries,
        valuationRange,
        includeInReport,
        exportAsWorkingPapers
      };
      
      setHasUnsavedChanges(false);
      setLastAutoSave(new Date());
      setAutoSaveStatus('saved');
      
      // Reset status after 3 seconds
      setTimeout(() => {
        setAutoSaveStatus('idle');
      }, 3000);
      
    } catch (err) {
      console.error('Auto-save error:', err);
      setAutoSaveStatus('error');
    }
  }, [inspectionId, editedEntries, valuationRange, includeInReport, exportAsWorkingPapers, hasUnsavedChanges, saving, disabled, token]);

  // Calculate totals for an entry (moved up for auto-save)
  const calculateEntryTotals = (entry) => {
    const locationAdj = parseFloat(entry.location_adjustment_percent) || 0;
    const sizeAdj = parseFloat(entry.size_adjustment_percent) || 0;
    const conditionAdj = parseFloat(entry.condition_adjustment_percent) || 0;
    
    const totalAdj = locationAdj + sizeAdj + conditionAdj;
    const adjustedPricePerSqm = entry.price_per_sqm ? entry.price_per_sqm * (1 + totalAdj / 100) : null;
    const adjustedValuation = entry.sale_price ? entry.sale_price * (1 + totalAdj / 100) : null;
    
    return {
      total_adjustment_percent: totalAdj,
      adjusted_price_per_sqm: adjustedPricePerSqm,
      adjusted_valuation: adjustedValuation
    };
  };

  // Setup auto-save interval
  useEffect(() => {
    if (!inspectionId || disabled) {
      return;
    }
    
    // Clear existing interval
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
    }
    
    // Set up new interval
    autoSaveIntervalRef.current = setInterval(() => {
      performAutoSave();
    }, AUTO_SAVE_INTERVAL);
    
    // Cleanup on unmount
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [inspectionId, disabled, performAutoSave]);

  // Save before leaving page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes in the Analysis Matrix. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Update a cell value
  const updateCell = (comparableId, field, value) => {
    setEditedEntries(prev => ({
      ...prev,
      [comparableId]: {
        ...prev[comparableId],
        [field]: value
      }
    }));
  };
  
  // Calculate adjustment based on comparison
  const suggestAdjustment = (comparison) => {
    if (comparison === 'superior') return -5; // Comparable is better, reduce value
    if (comparison === 'inferior') return 5;  // Comparable is worse, increase value
    return 0;
  };
  
  // Handle comparison change with auto-suggest
  const handleComparisonChange = (comparableId, field, value) => {
    updateCell(comparableId, field, value);
    
    // Auto-suggest adjustment percentage
    const adjustmentField = field.replace('_comparison', '_adjustment_percent');
    const currentEntry = editedEntries[comparableId];
    
    // Only suggest if no adjustment already set
    if (!currentEntry?.[adjustmentField]) {
      updateCell(comparableId, adjustmentField, suggestAdjustment(value));
    }
  };
  
  // Save entry changes
  const saveEntry = async (comparableId) => {
    const entry = editedEntries[comparableId];
    if (!entry) return;
    
    setSaving(true);
    try {
      // Calculate totals
      const totals = calculateEntryTotals(entry);
      const updatedEntry = { ...entry, ...totals };
      
      await axios.put(
        `${API}/analysis-matrix/${inspectionId}/entry/${comparableId}`,
        updatedEntry,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setEditedEntries(prev => ({
        ...prev,
        [comparableId]: updatedEntry
      }));
      
      toast.success('Analysis saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save analysis'));
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate valuation range
  const calculateRange = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(
        `${API}/analysis-matrix/${inspectionId}/calculate`,
        { subject_floor_area: subjectProperty?.floor_area_sqm },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const calc = response.data.calculation;
        setValuationRange(prev => ({
          ...prev,
          lowest_adjusted: calc.lowest_adjusted,
          highest_adjusted: calc.highest_adjusted,
          average_adjusted: calc.average_adjusted,
          weighted_average: calc.weighted_average,
          suggested_range_low: calc.suggested_range_low,
          suggested_range_high: calc.suggested_range_high
        }));
        toast.success('Valuation range calculated');
        
        if (onValuationCalculated) {
          onValuationCalculated(calc);
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to calculate valuation'));
    } finally {
      setCalculating(false);
    }
  };
  
  // Save valuation range
  const saveValuationRange = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/analysis-matrix/${inspectionId}/valuation-range`,
        valuationRange,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Valuation range saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save valuation range'));
    } finally {
      setSaving(false);
    }
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '-';
    return `£${Number(value).toLocaleString()}`;
  };
  
  // Get entries as array
  const entries = Object.values(editedEntries).sort((a, b) => a.position - b.position);
  
  if (loading) {
    return (
      <Card data-testid="analysis-matrix-loading">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading analysis matrix...</span>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card data-testid="comparable-analysis-matrix">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Comparable Analysis Matrix
            </CardTitle>
            <CardDescription>
              Analyse comparables and derive opinion of value. Click cells to edit.
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-save status indicator */}
            {!disabled && (
              <div className="flex items-center gap-2 text-sm">
                {autoSaveStatus === 'saving' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-blue-50 text-blue-700">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Auto-saving...
                  </Badge>
                )}
                {autoSaveStatus === 'saved' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-green-50 text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Saved
                  </Badge>
                )}
                {autoSaveStatus === 'error' && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-red-50 text-red-700">
                    <AlertTriangle className="h-3 w-3" />
                    Save failed
                  </Badge>
                )}
                {autoSaveStatus === 'idle' && hasUnsavedChanges && (
                  <Badge variant="secondary" className="flex items-center gap-1 bg-amber-50 text-amber-700">
                    <Clock className="h-3 w-3" />
                    Unsaved
                  </Badge>
                )}
                {autoSaveStatus === 'idle' && !hasUnsavedChanges && lastAutoSave && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last saved: {lastAutoSave.toLocaleTimeString()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {!disabled && (
          <p className="text-xs text-gray-500 mt-1">
            Auto-save enabled (every 30 seconds)
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Matrix Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="matrix-table">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left font-medium w-40">Attribute</th>
                <th className="border p-2 text-left font-medium bg-blue-50">Subject Property</th>
                {entries.map((entry, idx) => (
                  <th key={entry.comparable_id} className="border p-2 text-left font-medium min-w-48">
                    <div className="flex items-center justify-between">
                      <span>Comparable {idx + 1}</span>
                      {entry.hierarchy_ranking && (
                        <Badge variant="outline" className="text-xs">
                          {entry.hierarchy_ranking}
                        </Badge>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Address */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Address</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.address || matrix?.subject_property?.address || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.address || '-'}</td>
                ))}
              </tr>
              
              {/* Type */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Type</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.property_type?.replace(/_/g, ' ') || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.property_type?.replace(/_/g, ' ') || '-'}</td>
                ))}
              </tr>
              
              {/* Age */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Age</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.age || matrix?.subject_property?.age || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.age || '-'}</td>
                ))}
              </tr>
              
              {/* Beds */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Beds</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.bedrooms || matrix?.subject_property?.bedrooms || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.bedrooms || '-'}</td>
                ))}
              </tr>
              
              {/* Reception */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Reception</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.reception_rooms || matrix?.subject_property?.reception_rooms || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.reception_rooms || '-'}</td>
                ))}
              </tr>
              
              {/* Bathrooms */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Bathrooms</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.bathrooms || matrix?.subject_property?.bathrooms || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.bathrooms || '-'}</td>
                ))}
              </tr>
              
              {/* Floor Area */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Floor Area (m²)</td>
                <td className="border p-2 bg-blue-50/50">{subjectProperty?.floor_area_sqm || matrix?.subject_property?.floor_area_sqm || '-'}</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.floor_area_sqm || '-'}</td>
                ))}
              </tr>
              
              <tr><td colSpan={entries.length + 2} className="bg-gray-200 h-1"></td></tr>
              
              {/* Location Comparison - Editable */}
              <tr className="bg-amber-50/30">
                <td className="border p-2 bg-gray-50 font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Location
                </td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Select 
                      value={entry.location_comparison || ''} 
                      onValueChange={(v) => handleComparisonChange(entry.comparable_id, 'location_comparison', v)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPARISON_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                ))}
              </tr>
              
              {/* Location Adjustment % */}
              <tr className="bg-amber-50/30">
                <td className="border p-2 bg-gray-50 text-xs pl-6">Adjustment %</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Input
                      type="number"
                      value={entry.location_adjustment_percent || ''}
                      onChange={(e) => updateCell(entry.comparable_id, 'location_adjustment_percent', parseFloat(e.target.value) || 0)}
                      className="h-7 text-xs text-center"
                      placeholder="0"
                      disabled={disabled}
                    />
                  </td>
                ))}
              </tr>
              
              {/* Size Comparison - Editable */}
              <tr className="bg-green-50/30">
                <td className="border p-2 bg-gray-50 font-medium flex items-center gap-1">
                  <Ruler className="h-3 w-3" /> Size
                </td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Select 
                      value={entry.size_comparison || ''} 
                      onValueChange={(v) => handleComparisonChange(entry.comparable_id, 'size_comparison', v)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                ))}
              </tr>
              
              {/* Size Adjustment % */}
              <tr className="bg-green-50/30">
                <td className="border p-2 bg-gray-50 text-xs pl-6">Adjustment %</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Input
                      type="number"
                      value={entry.size_adjustment_percent || ''}
                      onChange={(e) => updateCell(entry.comparable_id, 'size_adjustment_percent', parseFloat(e.target.value) || 0)}
                      className="h-7 text-xs text-center"
                      placeholder="0"
                      disabled={disabled}
                    />
                  </td>
                ))}
              </tr>
              
              {/* Condition Comparison - Editable */}
              <tr className="bg-purple-50/30">
                <td className="border p-2 bg-gray-50 font-medium flex items-center gap-1">
                  <Wrench className="h-3 w-3" /> Condition
                </td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Select 
                      value={entry.condition_comparison || ''} 
                      onValueChange={(v) => handleComparisonChange(entry.comparable_id, 'condition_comparison', v)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPARISON_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                ))}
              </tr>
              
              {/* Condition Adjustment % */}
              <tr className="bg-purple-50/30">
                <td className="border p-2 bg-gray-50 text-xs pl-6">Adjustment %</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Input
                      type="number"
                      value={entry.condition_adjustment_percent || ''}
                      onChange={(e) => updateCell(entry.comparable_id, 'condition_adjustment_percent', parseFloat(e.target.value) || 0)}
                      className="h-7 text-xs text-center"
                      placeholder="0"
                      disabled={disabled}
                    />
                  </td>
                ))}
              </tr>
              
              <tr><td colSpan={entries.length + 2} className="bg-gray-200 h-1"></td></tr>
              
              {/* Sale Date */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Date of Sale</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.sale_date || '-'}</td>
                ))}
              </tr>
              
              {/* Sale Price */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Sale Price</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2 font-medium">{formatCurrency(entry.sale_price)}</td>
                ))}
              </tr>
              
              {/* £/m² */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">£/m²</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2">{entry.price_per_sqm ? `£${Math.round(entry.price_per_sqm).toLocaleString()}` : '-'}</td>
                ))}
              </tr>
              
              {/* Total Adjustment */}
              <tr className="bg-blue-50">
                <td className="border p-2 bg-gray-100 font-medium">Total Adjustment %</td>
                <td className="border p-2 bg-blue-100 text-center">—</td>
                {entries.map(entry => {
                  const totals = calculateEntryTotals(entry);
                  return (
                    <td key={entry.comparable_id} className="border p-2 font-medium text-center">
                      <span className={totals.total_adjustment_percent > 0 ? 'text-green-600' : totals.total_adjustment_percent < 0 ? 'text-red-600' : ''}>
                        {totals.total_adjustment_percent > 0 ? '+' : ''}{totals.total_adjustment_percent}%
                      </span>
                    </td>
                  );
                })}
              </tr>
              
              {/* Adjusted Valuation */}
              <tr className="bg-blue-100">
                <td className="border p-2 bg-gray-100 font-bold">Adjusted Valuation</td>
                <td className="border p-2 bg-blue-200 text-center">—</td>
                {entries.map(entry => {
                  const totals = calculateEntryTotals(entry);
                  return (
                    <td key={entry.comparable_id} className="border p-2 font-bold text-center">
                      {formatCurrency(totals.adjusted_valuation)}
                    </td>
                  );
                })}
              </tr>
              
              {/* Hierarchy Ranking */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Hierarchy</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Select 
                      value={entry.hierarchy_ranking || ''} 
                      onValueChange={(v) => updateCell(entry.comparable_id, 'hierarchy_ranking', v)}
                      disabled={disabled}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {HIERARCHY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                ))}
              </tr>
              
              {/* Notes */}
              <tr>
                <td className="border p-2 bg-gray-50 font-medium">Notes</td>
                <td className="border p-2 bg-blue-50/50 text-center">—</td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-1">
                    <Textarea
                      value={entry.valuer_notes || ''}
                      onChange={(e) => updateCell(entry.comparable_id, 'valuer_notes', e.target.value)}
                      className="text-xs min-h-[60px]"
                      placeholder="Analysis notes..."
                      disabled={disabled}
                    />
                  </td>
                ))}
              </tr>
              
              {/* Save Row */}
              <tr>
                <td className="border p-2 bg-gray-50"></td>
                <td className="border p-2 bg-blue-50/50"></td>
                {entries.map(entry => (
                  <td key={entry.comparable_id} className="border p-2 text-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => saveEntry(entry.comparable_id)}
                      disabled={saving || disabled}
                      className="text-xs"
                    >
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        
        {entries.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No comparables in schedule yet.</p>
            <p className="text-xs">Add comparables to the Comparable Schedule to begin analysis.</p>
          </div>
        )}
        
        <Separator />
        
        {/* Derived Valuation Range */}
        <div className="space-y-4" data-testid="valuation-range">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Derived Valuation Range
            </h3>
            <Button
              onClick={calculateRange}
              disabled={calculating || entries.length === 0 || disabled}
              size="sm"
            >
              {calculating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4 mr-2" />
              )}
              Calculate
            </Button>
          </div>
          
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded">
              <div className="text-xs text-muted-foreground">Lowest Adjusted</div>
              <div className="font-bold text-lg">{formatCurrency(valuationRange.lowest_adjusted)}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded">
              <div className="text-xs text-muted-foreground">Highest Adjusted</div>
              <div className="font-bold text-lg">{formatCurrency(valuationRange.highest_adjusted)}</div>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded">
              <div className="text-xs text-muted-foreground">Average Adjusted</div>
              <div className="font-bold text-lg">{formatCurrency(valuationRange.average_adjusted)}</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
              <div className="text-xs text-blue-600">Weighted Average</div>
              <div className="font-bold text-lg text-blue-700">{formatCurrency(valuationRange.weighted_average)}</div>
            </div>
          </div>
          
          {(valuationRange.suggested_range_low || valuationRange.suggested_range_high) && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded">
              <div className="text-sm font-medium text-amber-800">Suggested Valuation Range</div>
              <div className="text-lg font-bold text-amber-900">
                {formatCurrency(valuationRange.suggested_range_low)} - {formatCurrency(valuationRange.suggested_range_high)}
              </div>
            </div>
          )}
          
          <Separator />
          
          {/* Final Opinion of Value */}
          <div className="space-y-3">
            <h4 className="font-medium">Final Opinion of Value</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Market Value (£)</Label>
                <Input
                  type="number"
                  value={valuationRange.final_opinion_value || ''}
                  onChange={(e) => setValuationRange(prev => ({
                    ...prev,
                    final_opinion_value: e.target.value
                  }))}
                  placeholder="Enter final opinion..."
                  className="text-lg font-bold"
                  disabled={disabled}
                  data-testid="final-opinion-value"
                />
              </div>
              <div>
                <Label>Valuer Notes</Label>
                <Textarea
                  value={valuationRange.final_opinion_notes || ''}
                  onChange={(e) => setValuationRange(prev => ({
                    ...prev,
                    final_opinion_notes: e.target.value
                  }))}
                  placeholder="Justification for final value..."
                  disabled={disabled}
                />
              </div>
            </div>
            
            <Button
              onClick={saveValuationRange}
              disabled={saving || disabled}
              className="w-full"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save Valuation Range & Opinion
            </Button>
          </div>
          
          <Separator />
          
          {/* Export Options */}
          <div className="space-y-3" data-testid="export-options">
            <h4 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Report Export Options
            </h4>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeInReport}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setIncludeInReport(newValue);
                    try {
                      await axios.put(
                        `${API}/analysis-matrix/${inspectionId}`,
                        { include_in_report: newValue },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                    } catch (err) {
                      toast.error('Failed to update setting');
                    }
                  }}
                  disabled={disabled}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm">Include Matrix in Report Appendix</span>
              </label>
              
              {includeInReport && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exportAsWorkingPapers}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      setExportAsWorkingPapers(newValue);
                      try {
                        await axios.put(
                          `${API}/analysis-matrix/${inspectionId}`,
                          { export_as_working_papers: newValue },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                      } catch (err) {
                        toast.error('Failed to update setting');
                      }
                    }}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Mark as Working Papers</span>
                </label>
              )}
            </div>
            {includeInReport && (
              <p className="text-xs text-muted-foreground">
                Matrix will be exported as an appendix when generating the report.
                {exportAsWorkingPapers && " A working papers disclaimer will be included."}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
